import { useMemo } from 'react'
import { countAsUnreadIndicator } from '../../helpers/unreadBadge'
import { totalUnreadIndicator } from '../../redux/channels/selectors'
import { UnreadIndicator } from '../../redux/channels/unreadIndicator'
import { useSelector } from '../../redux/hooks'
import { unreadTaskCount } from '../../redux/myTasks/selectors'
import { MenuBadge } from './SideMenuConfig'

/**
 * Live indicators for every badge a menu item can declare.
 *
 * One place, because two things now render the same menu — the desktop rail and the mobile
 * tab bar — and a nav that disagrees with itself about how many unread messages you have
 * is worse than one that shows no count at all.
 *
 * Mentions deliberately fold into the unread total rather than getting their own counter:
 * the collapsed rail is ~40px wide and a tab is ~70px, so two competing numbers read as
 * noise at either size. Mentions are always a subset of unread, so this can never show
 * zero while a mention is waiting.
 *
 * Every `MenuBadge` has to be answered here, so adding one to that union is what forces a
 * count for it — which is how the mobile bar keeps up with a badge the desktop rail gains.
 */
export const useMenuBadgeCounts = (): Record<MenuBadge, UnreadIndicator> => {
  const unreadMessages = useSelector(totalUnreadIndicator)
  const unreadTasks = useSelector(unreadTaskCount)

  return useMemo(
    () => ({ unreadMessages, unreadTasks: countAsUnreadIndicator(unreadTasks) }),
    [unreadMessages, unreadTasks],
  )
}

export default useMenuBadgeCounts
